import {
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityDetailField } from '@/components/ui/entity-detail-field';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';

import { TARIFF_REFERENCES_QUERY } from './tariffs.gql.tsx';

interface TariffReferencesTabProps {
	tariffId: string;
}

export function TariffReferencesTab({ tariffId }: TariffReferencesTabProps) {
	const { t } = useTranslation('common');
	const { t: tT } = useTranslation('tariffs');

	const { data, loading, error } = useQuery(TARIFF_REFERENCES_QUERY, {
		variables: { tariffId }
	});

	const { shouldShowLoading } = useDelayedLoading(loading, {
		delay: 100,
		showInitialLoading: true
	});

	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="w-48 h-6 rounded bg-muted animate-pulse" />
				<div className="w-32 h-6 rounded bg-muted animate-pulse" />
				<div className="space-y-2">
					<div className="w-full h-4 rounded bg-muted animate-pulse" />
					<div className="w-3/4 h-4 rounded bg-muted animate-pulse" />
				</div>
			</div>
		);
	}

	if (!loading && error) {
		return (
			<div className="py-8 text-center text-muted-foreground">
				{tT('details.loadError')}
			</div>
		);
	}

	const pricingPoliciesAsPrimary = data?.pricingPoliciesAsPrimary?.nodes ?? [];
	const pricingPoliciesAsOverstay = data?.pricingPoliciesAsOverstay?.nodes ?? [];
	const vouchersWithTariff = data?.vouchersWithTariff?.nodes ?? [];
	const prepaidCardsWithTariff = data?.prepaidCardsWithTariff?.nodes ?? [];

	const hasPrimaryPolicies = pricingPoliciesAsPrimary.length > 0;
	const hasOverstayPolicies = pricingPoliciesAsOverstay.length > 0;
	const hasVouchers = vouchersWithTariff.length > 0;
	const hasPrepaidCards = prepaidCardsWithTariff.length > 0;
	const hasNoReferences = !hasPrimaryPolicies && !hasOverstayPolicies && !hasVouchers && !hasPrepaidCards;

	return (
		<div className="space-y-6">
			{hasPrimaryPolicies && (
				<DetailSection title={t('sections.pricing_policies')}>
					<p className="text-sm text-muted-foreground mb-4">
						{tT('references.asPrimaryTariff')}
					</p>
					<DetailFieldsGrid>
						{pricingPoliciesAsPrimary.map((policy: { pricingPolicyId: string; name: string }) => (
							<EntityDetailField
								key={policy.pricingPolicyId}
								label={tT('references.policy')}
								value={policy.name}
								entityId={policy.pricingPolicyId}
								entityType="pricingPolicy"
							/>
						))}
					</DetailFieldsGrid>
				</DetailSection>
			)}

			{hasOverstayPolicies && (
				<DetailSection title={t('sections.pricing_policies')}>
					<p className="text-sm text-muted-foreground mb-4">
						{tT('references.asOverstayTariff')}
					</p>
					<DetailFieldsGrid>
						{pricingPoliciesAsOverstay.map((policy: { pricingPolicyId: string; name: string }) => (
							<EntityDetailField
								key={policy.pricingPolicyId}
								label={tT('references.policy')}
								value={policy.name}
								entityId={policy.pricingPolicyId}
								entityType="pricingPolicy"
							/>
						))}
					</DetailFieldsGrid>
				</DetailSection>
			)}

			{hasVouchers && (
				<DetailSection title={tT('references.vouchersSection')}>
					<p className="text-sm text-muted-foreground mb-4">
						{tT('references.vouchersDescription')}
					</p>
					<DetailFieldsGrid>
						{vouchersWithTariff.map((voucher: { voucherId: string; name: string | null; code: string }) => (
							<EntityDetailField
								key={voucher.voucherId}
								label={tT('references.voucher')}
								value={voucher.name || voucher.code}
								entityId={voucher.voucherId}
								entityType="voucher"
							/>
						))}
					</DetailFieldsGrid>
				</DetailSection>
			)}

			{hasPrepaidCards && (
				<DetailSection title={tT('references.prepaidCardsSection')}>
					<p className="text-sm text-muted-foreground mb-4">
						{tT('references.prepaidCardsDescription')}
					</p>
					<DetailFieldsGrid>
						{prepaidCardsWithTariff.map((card: { prepaidCardId: string; prepaidCode: string }) => (
							<EntityDetailField
								key={card.prepaidCardId}
								label={tT('references.prepaidCard')}
								value={card.prepaidCode}
								entityId={card.prepaidCardId}
								entityType="prepaidCard"
							/>
						))}
					</DetailFieldsGrid>
				</DetailSection>
			)}

			{hasNoReferences && (
				<div className="py-8 text-center text-muted-foreground">
					{tT('references.noReferences')}
				</div>
			)}
		</div>
	);
}
